import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  X,
  Maximize,
  RotateCcw,
  Tags,
  Hand,
  Unlink,
  Search,
  Waypoints,
  ChevronUp,
  Users,
  Minus,
  Globe,
  Activity,
  SlidersHorizontal,
  HelpCircle,
} from "lucide-react";
import DateRangePicker from "../../../Universal/DateRangePicker";
import { safeIcon } from "../config/graphIcons";

const GraphBottomBar = memo(
  ({
    t,
    dark,
    datePickerTheme,
    graphSourceLabel,
    graphSourceMeta,
    teamMembers,
    switchToMyGraph,
    switchToTeamGraph,
    switchToGlobalGraph,
    barSearch,
    onSearchChange,
    onExecuteSearch,
    onClearSearch,
    onClearAllFilters,
    searchMatchedNodes,
    showEdgeLabels,
    setShowEdgeLabels,
    manualOnly,
    setManualOnly,
    hideOrphans,
    setHideOrphans,
    degreeHighlight,
    setDegreeHighlight,
    graphDateFrom,
    graphDateTo,
    setGraphDateFrom,
    setGraphDateTo,
    graphLimit,
    setGraphLimit,
    refetchWithLimit,
    handleZoomToFit,
    handleRelayout,
    handleZoomToNode,
  }) => {
    const cyan = "14,165,233";
    const cyanLit = "56,189,248";

    const tokens = {
      panelBg: dark ? "rgba(10,16,28,0.72)" : "rgba(255,255,255,0.78)",
      popupBg: dark ? "rgba(10,16,28,0.96)" : "rgba(255,255,255,0.98)",
      border: dark ? "rgba(148,163,184,0.14)" : "rgba(100,116,139,0.18)",
      softBorder: dark ? "rgba(148,163,184,0.10)" : "rgba(100,116,139,0.12)",
      divider: dark ? "rgba(148,163,184,0.10)" : "rgba(100,116,139,0.12)",
      primary: dark ? "rgba(226,232,240,0.90)" : "rgba(15,23,42,0.88)",
      muted: dark ? "rgba(148,163,184,0.62)" : "rgba(71,85,105,0.68)",
      faint: dark ? "rgba(148,163,184,0.35)" : "rgba(71,85,105,0.42)",
      accent: dark ? `rgb(${cyanLit})` : `rgb(${cyan})`,
      danger: dark ? "rgba(248,113,113,0.82)" : "rgba(220,38,38,0.82)",
      hoverBg: dark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.07)",
      activeBg: dark ? "rgba(14,165,233,0.12)" : "rgba(14,165,233,0.09)",
      activeBorder: dark ? "rgba(14,165,233,0.20)" : "rgba(14,165,233,0.16)",
      shadow: dark
        ? "0 18px 48px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.04)"
        : "0 18px 42px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.70)",
      popupShadow: dark
        ? "0 18px 44px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.04)"
        : "0 18px 38px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.75)",
    };

    const btn = (active = false) => ({
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 9px",
      borderRadius: 10,
      fontSize: 11,
      cursor: "pointer",
      transition: "all 0.15s ease",
      background: active ? tokens.activeBg : "transparent",
      border: `1px solid ${active ? tokens.activeBorder : "transparent"}`,
      color: active ? tokens.accent : tokens.muted,
      boxShadow: "none",
      whiteSpace: "nowrap",
    });

    const triggerControlStyle = {
      background: "transparent",
      border: `1px solid ${tokens.softBorder}`,
      color: tokens.muted,
      borderRadius: 10,
      outline: "none",
      boxShadow: "none",
      transition: "all 0.15s ease",
    };

    const iconBtn = (active = false) => ({
      ...btn(active),
      padding: "6px 8px",
    });

    const inputStyle = {
      ...triggerControlStyle,
    };

    const [graphSourceOpen, setGraphSourceOpen] = useState(false);
    const [graphSourceSearch, setGraphSourceSearch] = useState("");
    const [graphHelpOpen, setGraphHelpOpen] = useState(false);
    const [editingLimit, setEditingLimit] = useState(false);

    const limitTimerRef = useRef(null);
    const pendingLimitRef = useRef(graphLimit);

    const changeLimitBy = (delta) => {
      const v = Math.max(1, Math.min(5000, pendingLimitRef.current + delta));
      pendingLimitRef.current = v;
      setGraphLimit(v);

      if (limitTimerRef.current) clearTimeout(limitTimerRef.current);
      limitTimerRef.current = setTimeout(() => refetchWithLimit(v), 500);
    };

    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchTimerRef = useRef(null);
    const graphSourceRef = useRef(null);
    const graphHelpRef = useRef(null);
    const [localSearchText, setLocalSearchText] = useState(barSearch);

    useEffect(() => {
      setLocalSearchText(barSearch);
      if (!barSearch) {
        setSearchResults([]);
        setSearchLoading(false);
      }
    }, [barSearch]);

    useEffect(() => {
      if (!graphSourceOpen) return;

      const handler = (e) => {
        if (
          graphSourceRef.current &&
          !graphSourceRef.current.contains(e.target)
        ) {
          setGraphSourceOpen(false);
        }
      };

      document.addEventListener("pointerdown", handler, true);
      return () => document.removeEventListener("pointerdown", handler, true);
    }, [graphSourceOpen]);

    const handleLocalSearchChange = useCallback(
      (value) => {
        setLocalSearchText(value);
        onSearchChange(value);

        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        if (!value.trim()) {
          setSearchResults([]);
          setSearchLoading(false);
          searchTimerRef.current = setTimeout(() => onClearSearch(), 300);
          return;
        }

        setSearchLoading(true);
        searchTimerRef.current = setTimeout(() => {
          onExecuteSearch(value, (results) => {
            setSearchResults(results || []);
            setSearchLoading(false);
          });
        }, 400);
      },
      [onSearchChange, onClearSearch, onExecuteSearch],
    );

    const handleSearchKeyDown = useCallback(
      (e) => {
        if (e.key === "Enter" && localSearchText.trim()) {
          if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
          setSearchLoading(true);
          onExecuteSearch(localSearchText, (results) => {
            setSearchResults(results || []);
            setSearchLoading(false);
          });
        }

        if (e.key === "Escape") {
          setLocalSearchText("");
          setSearchResults([]);
          setSearchLoading(false);
          onClearSearch();
        }
      },
      [localSearchText, onExecuteSearch, onClearSearch],
    );

    const handleClearSearch = useCallback(() => {
      setLocalSearchText("");
      setSearchResults([]);
      setSearchLoading(false);

      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      onClearSearch();
    }, [onClearSearch]);

    const wrapSourceSwitch = useCallback(
      (fn) =>
        (...args) => {
          setGraphSourceOpen(false);
          setGraphSourceSearch("");
          setSearchResults([]);
          setLocalSearchText("");
          fn(...args);
        },
      [],
    );

    const Divider = () => (
      <div
        style={{
          width: 1,
          height: 20,
          background: tokens.divider,
          flexShrink: 0,
        }}
      />
    );

    const SourceRow = ({ children, active, onClick }) => (
      <button
        className="group/src flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors"
        style={{
          background: active ? tokens.activeBg : "transparent",
          borderLeft: `2px solid ${active ? tokens.activeBorder : "transparent"}`,
          color: tokens.primary,
          cursor: "pointer",
        }}
        onMouseEnter={(e) => {
          if (!active) e.currentTarget.style.background = tokens.hoverBg;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = active
            ? tokens.activeBg
            : "transparent";
        }}
        onClick={onClick}
      >
        {children}
      </button>
    );

    const SourceIcon = ({ icon: Icon, initial, active }) => (
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[10px] font-semibold"
        style={{
          background: active
            ? tokens.activeBg
            : dark
              ? "rgba(148,163,184,0.06)"
              : "rgba(100,116,139,0.06)",
          border: `1px solid ${active ? tokens.activeBorder : tokens.softBorder}`,
          color: active ? tokens.accent : tokens.faint,
        }}
      >
        {initial ? (
          <span>{initial}</span>
        ) : (
          <Icon size={15} strokeWidth={1.9} />
        )}
      </div>
    );

    const HelpSection = ({ title, children }) => (
      <div className="py-2">
        <div
          className="px-3.5 pb-1.5 pt-2 text-[10px] font-normal uppercase tracking-[0.14em]"
          style={{ color: tokens.muted }}
        >
          {title}
        </div>
        <div className="space-y-0.5">{children}</div>
      </div>
    );

    const HelpRow = ({ label, children }) => (
      <div
        className="grid grid-cols-[104px_1fr] gap-3 px-3.5 py-1.5"
        style={{ borderLeft: `2px solid transparent` }}
      >
        <div
          className="text-[10.5px] font-normal tracking-[0.01em]"
          style={{ color: tokens.primary, opacity: 0.78 }}
        >
          {label}
        </div>
        <div
          className="text-[11px] leading-relaxed"
          style={{ color: tokens.muted, opacity: 0.92 }}
        >
          {children}
        </div>
      </div>
    );

    const Key = ({ children }) => (
      <span
        className="mx-0.5 inline-flex items-center rounded-md px-1.5 py-[1px] text-[10px] font-normal"
        style={{
          color: tokens.primary,
          background: dark
            ? "rgba(148,163,184,0.08)"
            : "rgba(100,116,139,0.08)",
          border: `1px solid ${tokens.softBorder}`,
        }}
      >
        {children}
      </span>
    );

    const ColorSwatch = ({ color, label, children }) => (
      <div
        className="mt-1 flex items-start gap-2 rounded-lg px-2 py-1.5"
        style={{
          background: dark
            ? "rgba(148,163,184,0.045)"
            : "rgba(100,116,139,0.055)",
          border: `1px solid ${tokens.softBorder}`,
        }}
      >
        <span
          className="mt-[3px] h-2.5 w-2.5 shrink-0 rounded-full"
          style={{
            background: color,
            boxShadow: `0 0 10px ${color}`,
          }}
        />
        <span className="min-w-0 text-[11px] leading-relaxed">
          <span style={{ color: tokens.primary, opacity: 0.82 }}>{label}</span>
          <span style={{ color: tokens.muted, opacity: 0.9 }}>
            {" "}
            — {children}
          </span>
        </span>
      </div>
    );

    const LinkStyle = ({ color, dashed = false, label, children }) => (
      <div
        className="mt-1 flex items-start gap-2 rounded-lg px-2 py-1.5"
        style={{
          background: dark
            ? "rgba(148,163,184,0.045)"
            : "rgba(100,116,139,0.055)",
          border: `1px solid ${tokens.softBorder}`,
        }}
      >
        <span className="mt-[8px] flex h-0 w-9 shrink-0 items-center">
          <span
            className="block w-full"
            style={{
              borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}`,
              filter: `drop-shadow(0 0 5px ${color})`,
            }}
          />
        </span>
        <span className="min-w-0 text-[11px] leading-relaxed">
          <span style={{ color: tokens.primary, opacity: 0.82 }}>{label}</span>
          <span style={{ color: tokens.muted, opacity: 0.9 }}>
            {" "}
            — {children}
          </span>
        </span>
      </div>
    );

    const hasClearableFilters =
      Boolean(localSearchText) ||
      manualOnly ||
      hideOrphans ||
      degreeHighlight > 0;

    const handleClearFilters = () => {
      if (!hasClearableFilters) return;

      handleClearSearch();
      onClearAllFilters();
    };

    return (
      <>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }

          .gb-input::placeholder {
            color: ${tokens.muted};
            opacity: 1;
          }

          .gb-input:hover {
            background: ${tokens.hoverBg} !important;
            color: ${tokens.primary} !important;
          }

          .gb-input:focus {
            border-color: ${tokens.activeBorder} !important;
            background: ${tokens.activeBg} !important;
            color: ${tokens.primary} !important;
          }

          .gb-date-shell {
            color: ${tokens.muted};
          }

          .gb-date-shell:hover {
            background: ${tokens.hoverBg} !important;
          }

          .gb-date-shell input::placeholder {
            color: ${tokens.muted} !important;
            opacity: 1;
          }

          .gb-date-shell input,
          .gb-date-shell button {
            background: transparent !important;
            border-color: transparent !important;
            color: ${tokens.muted} !important;
            box-shadow: none !important;
          }

          .gb-date-shell input:hover,
          .gb-date-shell button:hover {
            background: transparent !important;
            color: ${tokens.primary} !important;
          }

          .gb-date-shell input:focus,
          .gb-date-shell button:focus,
          .gb-date-shell button[data-state="open"] {
            background: transparent !important;
            border-color: transparent !important;
            color: ${tokens.primary} !important;
            box-shadow: none !important;
          }

          .gb-date-shell:focus-within {
            border-color: ${tokens.activeBorder} !important;
            background: ${tokens.activeBg} !important;
          }
        `}</style>

        <div className="pointer-events-none absolute bottom-4 left-5 right-5 z-[55] flex flex-col items-center">
          <motion.div
            data-graph-ui-overlay="true"
            className="pointer-events-auto relative w-full max-w-[1060px] overflow-visible rounded-xl backdrop-blur-xl"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
            style={{
              background: tokens.panelBg,
              border: `1px solid ${tokens.border}`,
              boxShadow: tokens.shadow,
            }}
          >
            <div className="relative z-[1]">
              <div
                className="flex flex-wrap items-center gap-2 px-3.5 py-2.5"
                style={{
                  borderBottom: `1px solid ${tokens.divider}`,
                }}
              >
                <div
                  className="relative"
                  ref={graphSourceRef}
                  data-tutorial="graph-source"
                >
                  <button
                    style={{
                      ...btn(graphSourceOpen),
                      border: `1px solid ${
                        graphSourceOpen
                          ? tokens.activeBorder
                          : tokens.softBorder
                      }`,
                    }}
                    onMouseEnter={(e) => {
                      if (!graphSourceOpen) {
                        e.currentTarget.style.background = tokens.hoverBg;
                        e.currentTarget.style.color = tokens.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!graphSourceOpen) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = tokens.muted;
                      }
                    }}
                    onClick={() => setGraphSourceOpen((prev) => !prev)}
                  >
                    <Waypoints
                      size={13}
                      style={{
                        color: tokens.accent,
                        opacity: 0.86,
                        flexShrink: 0,
                      }}
                    />
                    <span className="max-w-[136px] truncate font-medium">
                      {graphSourceLabel}
                    </span>
                    <ChevronUp
                      size={13}
                      style={{
                        color: tokens.muted,
                        transform: graphSourceOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.15s ease",
                        flexShrink: 0,
                      }}
                    />
                  </button>

                  {graphSourceOpen &&
                    (() => {
                      const srcQ = graphSourceSearch.trim().toLowerCase();
                      const meta = graphSourceMeta.current || {};

                      const graphItems = [
                        // edited by cl
                        // {
                        //   key: "my_graph",
                        //   label: "My Graph",
                        //   sublabel: "Your graph workspace",
                        //   icon: Activity,
                        //   active: meta.kind === "my_graph",
                        //   onClick: wrapSourceSwitch(switchToMyGraph),
                        // },
                        {
                          key: "global",
                          label: "Global",
                          sublabel: "All graph data",
                          icon: Globe,
                          active: meta.kind === "global",
                          onClick: wrapSourceSwitch(switchToGlobalGraph),
                        },
                        // edited by cl
                        // ...teamMembers.map((member) => {
                        //   const fullName = `${member.first_name || ""} ${
                        //     member.last_name || ""
                        //   }`.trim();
                        //
                        //   return {
                        //     key: `team-${member.uuid}`,
                        //     label: fullName || "Team Graph",
                        //     sublabel: "Team graph",
                        //     icon: Users,
                        //     initial: (
                        //       member.first_name?.[0] || "?"
                        //     ).toUpperCase(),
                        //     active:
                        //       meta.kind === "team" && meta.uuid === member.uuid,
                        //     onClick: wrapSourceSwitch(() =>
                        //       switchToTeamGraph(member),
                        //     ),
                        //   };
                        // }),
                      ].filter((item) => {
                        if (!srcQ) return true;
                        return `${item.label} ${item.sublabel}`
                          .toLowerCase()
                          .includes(srcQ);
                      });

                      return (
                        <div
                          className="absolute bottom-full left-0 mb-2 w-[300px] overflow-hidden rounded-xl backdrop-blur-xl"
                          style={{
                            background: tokens.popupBg,
                            border: `1px solid ${tokens.border}`,
                            boxShadow: tokens.popupShadow,
                            zIndex: 9999,
                          }}
                        >
                          <div className="p-2">
                            <div className="relative">
                              <Search
                                size={13}
                                style={{
                                  position: "absolute",
                                  left: 9,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  color: tokens.faint,
                                }}
                              />
                              <input
                                type="text"
                                autoFocus
                                className="gb-input w-full rounded-lg py-1.5 pl-8 pr-3 text-xs outline-none"
                                style={inputStyle}
                                placeholder="Search graphs..."
                                value={graphSourceSearch}
                                onChange={(e) =>
                                  setGraphSourceSearch(e.target.value)
                                }
                              />
                            </div>
                          </div>

                          <div
                            className="max-h-[280px] overflow-y-auto pb-1"
                            style={{ scrollbarWidth: "thin" }}
                          >
                            {graphItems.length === 0 && (
                              <div
                                className="px-3 py-3 text-xs"
                                style={{ color: tokens.muted }}
                              >
                                No graphs found
                              </div>
                            )}

                            {graphItems.map((item) => (
                              <SourceRow
                                key={item.key}
                                active={item.active}
                                onClick={item.onClick}
                              >
                                <SourceIcon
                                  icon={item.icon}
                                  initial={item.initial}
                                  active={item.active}
                                />

                                <div className="min-w-0 flex-1">
                                  <div
                                    className="truncate text-[12px] font-medium"
                                    style={{ color: tokens.primary }}
                                  >
                                    {item.label}
                                  </div>
                                  <div
                                    className="truncate text-[10px]"
                                    style={{ color: tokens.faint }}
                                  >
                                    {item.sublabel}
                                  </div>
                                </div>
                              </SourceRow>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                </div>

                <Divider />

                <div
                  className="relative min-w-[260px] max-w-[470px] flex-1"
                  data-tutorial="search-bar"
                >
                  {searchLoading ? (
                    <div
                      style={{
                        position: "absolute",
                        left: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 13,
                        height: 13,
                        border: `2px solid rgba(${cyan},0.20)`,
                        borderTop: `2px solid rgba(${cyan},0.78)`,
                        borderRadius: "50%",
                        animation: "spin 0.6s linear infinite",
                      }}
                    />
                  ) : (
                    <Search
                      size={14}
                      style={{
                        position: "absolute",
                        left: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color:
                          searchMatchedNodes.size > 0
                            ? tokens.accent
                            : tokens.faint,
                      }}
                    />
                  )}

                  <input
                    type="text"
                    className="gb-input w-full text-xs outline-none"
                    style={{
                      ...inputStyle,
                      paddingTop: 6,
                      paddingBottom: 6,
                      paddingLeft: 32,
                      paddingRight: 28,
                      borderColor:
                        searchMatchedNodes.size > 0
                          ? tokens.activeBorder
                          : tokens.softBorder,
                      background:
                        searchMatchedNodes.size > 0
                          ? tokens.activeBg
                          : "transparent",
                      color:
                        searchMatchedNodes.size > 0
                          ? tokens.primary
                          : tokens.muted,
                    }}
                    placeholder={`Search ${
                      graphSourceLabel === "My Graph"
                        ? "my graph"
                        : graphSourceLabel === "Global"
                          ? "all public"
                          : graphSourceLabel.replace("'s Graph", "")
                    }...`}
                    value={localSearchText}
                    onChange={(e) => handleLocalSearchChange(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                  />

                  {localSearchText && (
                    <button
                      className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer rounded p-0.5 transition-colors"
                      style={{ color: tokens.faint }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = tokens.accent;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = tokens.faint;
                      }}
                      onClick={handleClearSearch}
                    >
                      <X size={13} />
                    </button>
                  )}

                  {searchResults.length > 0 && localSearchText && (
                    <div
                      className="absolute bottom-full left-0 mb-2 w-full overflow-hidden rounded-xl backdrop-blur-xl"
                      style={{
                        background: tokens.popupBg,
                        border: `1px solid ${tokens.border}`,
                        boxShadow: tokens.popupShadow,
                        zIndex: 9999,
                      }}
                    >
                      <div
                        className="max-h-[210px] overflow-y-auto"
                        style={{ scrollbarWidth: "thin" }}
                      >
                        {searchResults.map((m, idx) => {
                          const iconColor =
                            (t.node.typeColors &&
                              t.node.typeColors[m.icon || m.group]) ||
                            t.node.iconColor;

                          return (
                            <button
                              key={m.name + idx}
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors"
                              style={{
                                background: "transparent",
                                color: tokens.primary,
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                  tokens.hoverBg;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background =
                                  "transparent";
                              }}
                              onClick={() => handleZoomToNode(m.name)}
                            >
                              {React.createElement(
                                safeIcon(m.icon || m.group),
                                {
                                  size: 13,
                                  style: {
                                    color: iconColor,
                                    flexShrink: 0,
                                  },
                                },
                              )}
                              <span className="truncate">{m.name}</span>
                              <span
                                className="ml-auto shrink-0 text-[11px]"
                                style={{ color: tokens.faint }}
                              >
                                {(m.icon || m.group || "")
                                  .replace(/_/g, " ")
                                  .toLowerCase()}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <Divider />

                <div
                  className="gb-date-shell rounded-[10px] transition-all"
                  style={{
                    background: "transparent",
                    border: `1px solid ${tokens.softBorder}`,
                    color: tokens.muted,
                  }}
                >
                  <DateRangePicker
                    dateFrom={graphDateFrom}
                    dateTo={graphDateTo}
                    onChange={(from, to) => {
                      setGraphDateFrom(from);
                      setGraphDateTo(to);
                    }}
                    t={{
                      ...datePickerTheme,
                      border: "transparent",
                      inputBg: "transparent",
                      background: "transparent",
                      color: tokens.muted,
                      text: tokens.muted,
                      muted: tokens.muted,
                      accent: tokens.accent,
                    }}
                    compact
                  />
                </div>

                <Divider />

                <div
                  className="flex items-center gap-1"
                  data-tutorial="action-buttons"
                >
                  {[
                    { icon: Maximize, label: "Fit", action: handleZoomToFit },
                    {
                      icon: RotateCcw,
                      label: "Relayout",
                      action: handleRelayout,
                    },
                  ].map((action) => (
                    <button
                      key={action.label}
                      style={iconBtn(false)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = tokens.hoverBg;
                        e.currentTarget.style.color = tokens.accent;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = tokens.muted;
                      }}
                      onClick={action.action}
                    >
                      {React.createElement(action.icon, { size: 13 })}
                      <span>{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div
                className="flex flex-wrap items-center gap-2 px-3.5 py-2"
                data-tutorial="filter-row"
              >
                {[
                  {
                    id: "link-labels",
                    icon: Tags,
                    label: "Hide Link Labels",
                    active: !showEdgeLabels,
                    action: () => setShowEdgeLabels(!showEdgeLabels),
                  },
                  {
                    id: "manual-only",
                    icon: Hand,
                    label: "Hide Manual Nodes",
                    active: manualOnly,
                    action: () => setManualOnly(!manualOnly),
                  },
                  {
                    id: "hide-orphans",
                    icon: Unlink,
                    label: "Hide Orphans",
                    active: hideOrphans,
                    action: () => setHideOrphans(!hideOrphans),
                  },
                ].map((filter) => (
                  <button
                    key={filter.id}
                    style={btn(filter.active)}
                    onMouseEnter={(e) => {
                      if (!filter.active) {
                        e.currentTarget.style.background = tokens.hoverBg;
                        e.currentTarget.style.color = tokens.accent;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!filter.active) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = tokens.muted;
                      }
                    }}
                    onClick={filter.action}
                  >
                    {React.createElement(filter.icon, { size: 13 })}
                    <span>{filter.label}</span>
                  </button>
                ))}

                <div
                  className="ml-1 flex items-center gap-2 rounded-lg px-2 py-1"
                  style={{ color: tokens.muted }}
                >
                  <SlidersHorizontal
                    size={13}
                    style={{ color: tokens.faint }}
                  />
                  <span className="text-[10px] font-medium uppercase tracking-[0.10em]">
                    Degree ≥
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={10}
                    value={degreeHighlight}
                    onChange={(e) => setDegreeHighlight(+e.target.value)}
                    className="h-1 w-16 cursor-pointer accent-sky-500"
                  />
                  <span className="w-4 text-right text-xs tabular-nums">
                    {degreeHighlight}
                  </span>
                </div>

                <button
                  disabled={!hasClearableFilters}
                  style={{
                    ...btn(hasClearableFilters),
                    border: hasClearableFilters
                      ? dark
                        ? "1px solid rgba(248,113,113,0.18)"
                        : "1px solid rgba(220,38,38,0.14)"
                      : "1px solid transparent",
                    color: hasClearableFilters ? tokens.danger : tokens.faint,
                    background: hasClearableFilters
                      ? dark
                        ? "rgba(239,68,68,0.07)"
                        : "rgba(239,68,68,0.05)"
                      : "transparent",
                    cursor: hasClearableFilters ? "pointer" : "not-allowed",
                    opacity: hasClearableFilters ? 1 : 0.58,
                  }}
                  className="disabled:pointer-events-none"
                  onClick={handleClearFilters}
                >
                  <X size={12} />
                  <span>Clear</span>
                </button>

                <div
                  className="ml-auto flex items-center gap-1"
                  data-tutorial="limit-control"
                  title="Max nodes returned"
                >
                  <span
                    className="mr-1 text-[10px] font-medium uppercase tracking-[0.10em]"
                    style={{ color: tokens.faint }}
                  >
                    Limit
                  </span>

                  {editingLimit ? (
                    <input
                      type="number"
                      autoFocus
                      min={1}
                      max={5000}
                      defaultValue={graphLimit}
                      className="gb-input text-xs"
                      style={{ ...inputStyle, width: 64, padding: "3px 7px" }}
                      onBlur={(e) => {
                        const v = Math.max(
                          1,
                          Math.min(5000, parseInt(e.target.value, 10) || 100),
                        );
                        pendingLimitRef.current = v;
                        setGraphLimit(v);
                        refetchWithLimit(v);
                        setEditingLimit(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                        if (e.key === "Escape") setEditingLimit(false);
                      }}
                    />
                  ) : (
                    <div
                      className="flex items-center gap-0.5 rounded-lg"
                      style={{ border: `1px solid ${tokens.softBorder}` }}
                    >
                      <button
                        className="cursor-pointer rounded p-1 transition-colors"
                        style={{ color: tokens.faint }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = tokens.accent;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = tokens.faint;
                        }}
                        onClick={() => changeLimitBy(-25)}
                      >
                        <Minus size={12} />
                      </button>
                      <button
                        className="cursor-pointer rounded px-1.5 py-0.5 text-xs tabular-nums transition-colors"
                        style={{
                          color: tokens.muted,
                          minWidth: 36,
                          textAlign: "center",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = tokens.accent;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = tokens.muted;
                        }}
                        onClick={() => setEditingLimit(true)}
                      >
                        {graphLimit}
                      </button>
                      <button
                        className="cursor-pointer rounded p-1 transition-colors"
                        style={{ color: tokens.faint }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = tokens.accent;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = tokens.faint;
                        }}
                        onClick={() => changeLimitBy(25)}
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  )}
                </div>

                <div
                  ref={graphHelpRef}
                  className="relative flex items-center"
                  data-tutorial="graph-help"
                >
                  <button
                    type="button"
                    aria-label="Graph help"
                    title="Graph help"
                    style={{
                      ...iconBtn(graphHelpOpen),
                      border: `1px solid ${
                        graphHelpOpen ? tokens.activeBorder : tokens.softBorder
                      }`,
                      color: graphHelpOpen ? tokens.accent : tokens.muted,
                    }}
                    onMouseEnter={(e) => {
                      if (!graphHelpOpen) {
                        e.currentTarget.style.background = tokens.hoverBg;
                        e.currentTarget.style.color = tokens.accent;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!graphHelpOpen) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = tokens.muted;
                      }
                    }}
                    onClick={() => setGraphHelpOpen((prev) => !prev)}
                  >
                    <HelpCircle size={13} />
                    <span>Help</span>
                  </button>

                  {graphHelpOpen && (
                    <div
                      className="pointer-events-auto absolute bottom-full right-0 mb-2 w-[430px] overflow-hidden rounded-xl backdrop-blur-xl"
                      style={{
                        background: tokens.popupBg,
                        border: `1px solid ${tokens.border}`,
                        boxShadow: tokens.popupShadow,
                        zIndex: 9999,
                      }}
                    >
                      <div
                        className="flex items-center gap-2 px-3.5 py-2.5"
                        style={{ borderBottom: `1px solid ${tokens.divider}` }}
                      >
                        <div
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                          style={{
                            color: tokens.accent,
                            background: tokens.activeBg,
                            border: `1px solid ${tokens.activeBorder}`,
                          }}
                        >
                          <HelpCircle size={14} />
                        </div>
                        <div className="min-w-0">
                          <div
                            className="text-[11px] font-normal tracking-[0.08em]"
                            style={{ color: tokens.primary }}
                          >
                            Graph help
                          </div>
                          <div
                            className="text-[10px]"
                            style={{ color: tokens.faint }}
                          >
                            Keep this open while you navigate the graph
                          </div>
                        </div>
                      </div>

                      <div
                        className="max-h-[min(560px,70vh)] overflow-y-auto py-1"
                        style={{ scrollbarWidth: "thin" }}
                      >
                        <HelpSection title="Graph actions">
                          <HelpRow label="Inspect">
                            Click a node to inspect it in the right sidebar.
                          </HelpRow>
                          <HelpRow label="Multi-select">
                            Use <Key>Cmd</Key>/<Key>Ctrl</Key> + click to select
                            or remove a node cluster.
                          </HelpRow>
                          <HelpRow label="Box select">
                            Hold <Key>Cmd</Key>/<Key>Ctrl</Key> and drag on
                            empty graph space to select an area.
                          </HelpRow>
                          <HelpRow label="Create node">
                            Double-click empty graph space to create a
                            standalone node.
                          </HelpRow>
                          <HelpRow label="Add / connect">
                            Right-click a node to add a child node or connect it
                            to another node.
                          </HelpRow>
                        </HelpSection>

                        <div
                          style={{
                            height: 1,
                            background: tokens.divider,
                            margin: "2px 14px",
                          }}
                        />

                        <HelpSection title="Controls">
                          <HelpRow label="Date range">
                            Limits results to the selected time window.
                          </HelpRow>
                          <HelpRow label="Fit">
                            Centers the current graph and fits it into view.
                          </HelpRow>
                          <HelpRow label="Relayout">
                            Releases pinned positions and reruns the layout.
                          </HelpRow>
                          <HelpRow label="Degree">
                            Highlights nodes with at least this many
                            connections.
                          </HelpRow>
                          <HelpRow label="Limit">
                            Controls the maximum number of nodes requested from
                            the graph endpoint.
                          </HelpRow>
                        </HelpSection>

                        <div
                          style={{
                            height: 1,
                            background: tokens.divider,
                            margin: "2px 14px",
                          }}
                        />

                        <HelpSection title="Filters and display">
                          <HelpRow label="Node icons">
                            Icons reflect the node type coming from the graph
                            data, like person, location, organization, event, or
                            document.
                          </HelpRow>
                          <HelpRow label="Show / hide link labels">
                            Toggles the relationship text drawn between
                            connected nodes, such as “mentions,” “works at,” or
                            “located in.”
                          </HelpRow>
                          <HelpRow label="Hide manual nodes">
                            Hides user-created/manual nodes from the graph.
                            Leave it off to show all generated and manual nodes
                            together.
                          </HelpRow>
                          <HelpRow label="Hide / show orphans">
                            Hides orphan nodes, meaning nodes with no links to
                            anything else in the current graph. Toggle it off to
                            show them again.
                          </HelpRow>
                          <HelpRow label="Link style">
                            <LinkStyle
                              color="rgba(14,165,233,0.55)"
                              dashed
                              label="Dashed"
                            >
                              Generated relationships returned by the graph
                              data.
                            </LinkStyle>
                            <LinkStyle
                              color="rgba(14,165,233,0.70)"
                              label="Solid"
                            >
                              Manual relationships created by users.
                            </LinkStyle>
                          </HelpRow>
                        </HelpSection>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </>
    );
  },
);

GraphBottomBar.displayName = "GraphBottomBar";
export default GraphBottomBar;
